/**
 * Org-level settings (R17 — Founder-mode navigation preset).
 *
 * Backend: user-service `GET|PATCH /api/v1/orgs/{tenant_id}/settings` (gateway
 * prefix `/api/v1/orgs`). `me` resolves to the caller's own tenant server-side.
 * Reads: any member. Writes: org owner/admin (403 otherwise).
 */
import { api } from '@/lib/api/client';

export const WORKSPACE_PRESETS = ['founder', 'team', 'agency', 'enterprise'] as const;
export type WorkspacePreset = (typeof WORKSPACE_PRESETS)[number];
export const DEFAULT_WORKSPACE_PRESET: WorkspacePreset = 'team';

export interface OrgSettings {
  tenant_id: string;
  workspace_preset: WorkspacePreset;
  /** explicit | onboarding | default */
  preset_source: string;
  available_presets: WorkspacePreset[];
  updated_at?: string | null;
  updated_by?: string | null;
}

export function isWorkspacePreset(v: unknown): v is WorkspacePreset {
  return typeof v === 'string' && (WORKSPACE_PRESETS as readonly string[]).includes(v);
}

export function getOrgSettings(tenantId = 'me'): Promise<OrgSettings> {
  return api.get<OrgSettings>(`/api/v1/orgs/${encodeURIComponent(tenantId)}/settings`);
}

export function setWorkspacePreset(preset: WorkspacePreset, tenantId = 'me'): Promise<OrgSettings> {
  return api.patch<OrgSettings>(`/api/v1/orgs/${encodeURIComponent(tenantId)}/settings`, {
    workspace_preset: preset,
  });
}

/**
 * R1 hook (onboarding wizard, owned by increment I2): derive the preset from
 * the founder's stage answer server-side (`exploring | building |
 * built_no_users | selling` → founder, anything else → team).
 */
export function setWorkspacePresetFromStage(stage: string, tenantId = 'me'): Promise<OrgSettings> {
  return api.patch<OrgSettings>(`/api/v1/orgs/${encodeURIComponent(tenantId)}/settings`, {
    from_stage: stage,
  });
}
